import ValidateModule from "./ValidateModule";

import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { IEventChannel } from "../model/Models";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class BufferModule
 * 
 * Object providing Buffer operations
 */
export default class AsyncTasksModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.createRadius = this.createRadius.bind(this);
        this.createAllRadius = this.createAllRadius.bind(this);
        this.getRadiusGeometry = this.getRadiusGeometry.bind(this);
        this.createDriveTime = this.createDriveTime.bind(this);
        this.createAllDriveTime = this.createAllDriveTime.bind(this);
        this.getDriveTimeGeometry = this.getDriveTimeGeometry.bind(this);
        this.checkLimit = this.checkLimit.bind(this);
    }

    /**
    * Create a radius buffer on a given Feature
    * 
    * @param targetLayerViewId The layerViewId where the radius will be created
    * @param sourceLayerViewId The LayerViewId of the given Feature
    * @param featureId The Id of the Feature to generate a radius on
    * @param distances The distance for the Radius
    * @param unit The unit for the distance (KM, MILES, ...)
    * @param opts The option for the Ajax request
    * 
    * @returns
    */
    createRadius(targetLayerViewId: number, sourceLayerViewId: number, featureId: number, distances: number[], unit: string, opts?: ApiOpts) {
        if (!ValidateModule.checkIds([targetLayerViewId, sourceLayerViewId, featureId])) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArg(distances) || !ValidateModule.checkMandatoryArg(unit)) {
            return es.util.deferred.rejected();
        }

        opts = $.extend(opts, { resolveWithXHR: true});
        return this.rqBroker.doPost({
            url: es.util.url.fromParams({
                distances: distances,
                unit: unit
            }, es.api.getURL(this.resourceName) + '/radius/' + targetLayerViewId + '/' + sourceLayerViewId + '/' + featureId + '?'),
            opts: opts

        }).done((rs, jqXHR) => {
            const lastModified = jqXHR.getResponseHeader("es_feature_source_info_last_modified");
            
            this.cache?.clearAll();
            this.eventChannel?.fireEvent("onBatchEditFeatures", rs.length, targetLayerViewId, false, lastModified);
        });
    }

    /**
     * Create radius buffer for every Feature of the given LayerViewId
     * 
     * @param targetLayerViewId The layerViewId where the radius will be created
     * @param sourceLayerViewId The LayerViewId to use to generate Radius buffer on all his Feature
     * @param distances The distance for the Radius
     * @param unit The unit for the distance (KM, MILES, ...)
     * @param opts The option for the Ajax request
     * @param criteria (optional) criteria to be applied to the feature selection (replaces existing layerView criteria)
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    createAllRadius(targetLayerViewId: number, sourceLayerViewId: number, distances: number[], criteria, unit: string, opts?: ApiOpts) {
        if (!ValidateModule.checkIds([targetLayerViewId, sourceLayerViewId])) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArg(distances) || !ValidateModule.checkMandatoryArg(unit)) {
            return es.util.deferred.rejected();
        }
        
        this.cache.clear('AsyncTasks');
        
        var url = es.util.url.fromParams({
            distances: distances,
            unit: unit
        }, es.api.getURL(this.resourceName) + '/radius/' + targetLayerViewId + '/' + sourceLayerViewId + '?');

        if (criteria) {
            url+= "&criteria=" + es.util.url.stringify(criteria);
        }

        return this.rqBroker.doPost({
            url: url,
            opts: opts
        }).done(() => {
            this.cache.clearAll();
        });
    }

    /**
     * 
     * @param origin 
     * @param distance 
     * @param unit 
     * @param opts 
     * 
     * @returns 
     */
    getRadiusGeometry(origin, distance, unit, opts?: ApiOpts) {
        if(!ValidateModule.checkMandatoryArg(distance) || !ValidateModule.checkMandatoryArg(unit) || !ValidateModule.checkMandatoryArg(origin)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doGet({
            url: es.util.url.fromParams({
                origin: origin,
                srid: 4326,
                distance: distance,
                unit: unit
            }, es.api.getURL(this.resourceName) + '/radius/geometry' + '?'),
            opts: opts
        });
    }

    /**
     * Create drive time buffer for every Feature of the given LayerViewId
     * 
     * @param targetLayerViewId The layerViewId where the radius will be created
     * @param sourceLayerViewId The LayerViewId of the given Feature
     * @param featureId The Id of the Feature to generate a radius on
     * @param times The drive time for the given buffer
     * @param opts The option for the Ajax request
     * 
     * @returns
     */
    createDriveTime(targetLayerViewId: number, sourceLayerViewId: number, featureId: number, times: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds([targetLayerViewId, sourceLayerViewId, featureId])) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArg(times)) {
            return es.util.deferred.rejected();
        }

        opts = $.extend(opts, { resolveWithXHR: true});
        return this.rqBroker.doPost({
            url: es.util.url.fromParams({
                times: times
            }, es.api.getURL(this.resourceName) + '/drivetime/' + targetLayerViewId + '/' + sourceLayerViewId + '/' + featureId + '?'),
            opts: opts

        }).done((rs, jqXHR) => {
            const lastModified = jqXHR.getResponseHeader("es_feature_source_info_last_modified");
            this.cache?.clearAll();
            this.eventChannel?.fireEvent("onBatchEditFeatures", rs.length, targetLayerViewId, false, lastModified);
        });
    }

    /**
     * Create drive time buffer for every Feature of the given LayerViewId
     * 
     * @param targetLayerViewId The layerViewId where the radius will be created
     * @param sourceLayerViewId The LayerViewId of the given Feature
     * @param times The drive time for the given buffer
     * @param criteria (optional) criteria to be applied to the feature selection (replaces existing layerView criteria)
     * @param opts The option for the Ajax request
     * 
     * @returns
     */
    createAllDriveTime(targetLayerViewId: number, sourceLayerViewId: number, times: number[], criteria, opts?: ApiOpts) {
        if(!ValidateModule.checkIds([targetLayerViewId, sourceLayerViewId])) {
            return es.util.deferred.rejected();
        }
        if(!ValidateModule.checkMandatoryArg(times)) {
            return es.util.deferred.rejected();
        }
        
        this.cache.clear('AsyncTasks');
        
        let url = es.util.url.fromParams({
            times: times
        }, es.api.getURL(this.resourceName) + '/drivetime/' + targetLayerViewId + '/' + sourceLayerViewId + '?');

        if (criteria) {
            url+= "&criteria=" + es.util.url.stringify(criteria);
        }

        return this.rqBroker.doPost({
            url: url,
            opts: opts

        }).done(() => {
            this.cache?.clearAll();
        });
    }

    /**
     * 
     * @param origin 
     * @param time 
     * @param opts 
     * 
     * @returns 
     */
    getDriveTimeGeometry(origin, time, opts?: ApiOpts) {
        if(!ValidateModule.checkMandatoryArg(time) || !ValidateModule.checkMandatoryArg(origin)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doGet({
            url: es.util.url.fromParams({
                origin: origin,
                srid: 4326,
                time: time
            }, es.api.getURL(this.resourceName) + '/drivetime/geometry' + '?'),
            opts: opts
        });
    }

    /**
     * Check the limits for buffers
     * based on spatial properties in server config
     */
    checkLimit(valueStr: string, measure: string): string | null {
        const IS_VALID_NUMBER_REGEXP = new RegExp("^\\+?(([0-9]+\\.?[0-9]*)|(\\.[0-9]+))$");
        const MAX_KMS = es.spatialLimits.maxDistanceKilometer;
        const MAX_FEET_US = es.spatialLimits.maxDistanceFoot;
        const MAX_FEET = es.spatialLimits.maxDistanceFoot;
        const MAX_METERS = es.spatialLimits.maxDistanceMeter;
        const MAX_MILES = es.spatialLimits.maxDistanceMile;
        const MAX_MIN = es.spatialLimits.maxTime;
        const value = parseFloat(valueStr);

        if (!IS_VALID_NUMBER_REGEXP.test(valueStr + "") || isNaN(value) || value <= 0) { // last 2 conditions should never happen, regexp should fail... keeping it for now...
            return $i18n('_Limits_Buffers_Value_Unsupported');
        }

        if (measure === "SURVEY_FOOT" && value > MAX_FEET_US) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_FEET_US + " " + $i18n('_Feet_US') + ".");
        }

        if (measure === "FOOT" && value > MAX_FEET) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_FEET + " " + $i18n('Feet') + ".");
        }

        if (measure === "METER" && value > MAX_METERS) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_METERS + " " + $i18n('Meters') + ".");
        }

        if (measure === "KILOMETER" && value > MAX_KMS) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_KMS + " " + $i18n('Kms') + ".");
        }

        if (measure === "MILE" && value > MAX_MILES) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_MILES + " " + $i18n('Miles') + ".");
        }

        if (measure === "MIN" && value >  MAX_MIN) {
            return ( $i18n('_Analyze_Map_Drive_Time_Error_Msg_Time_Too_Big', [MAX_MIN]));
        }

        if (measure === "MIN" && parseInt(valueStr) != value) {
            return ($i18n('_Limits_Buffers_DriveTime_Error_Msg_Decimal_Values_Are_Not_Allowed'));
        }

        return null;
    }
}
